/**
 * Astryx 테마 카탈로그 — peeka 톤앤매너.
 *
 * 배포 테마는 **y2k 하나**다 (2026-08-05, stone 에서 교체). y2k 만 정적
 * import(`/built` + theme.css)로 번들에 들어가고, 나머지 6종은 **개발 중 톤앤매너
 * 비교용**이라 dynamic import 로 분리한다 (`import.meta.env.DEV` 가드 →
 * 프로덕션 번들에는 포함되지 않음).
 *
 * theme.css 는 global.css 에서 한 번 import 한다 (y2k 만).
 * 나머지 테마는 source import 의 런타임 주입 경로를 쓰므로 CSS 파일이 필요 없다.
 *
 * y2k 는 gothic 과 달리 **라이트/다크 양쪽 팔레트가 있다** — darkOnly 가 아니다.
 *
 * ⚠ y2k 는 stone 보다 타이포·간격 스케일이 한 단 크다(본문 14→16px, --spacing-3
 *   12→18px). 좁은 폭에서 잘리기 쉬우니 레이아웃 변경 후엔 375px 에서 확인할 것.
 * ⚠ **서체만** y2k 정본(Poppins)을 쓰지 않는다 — tokens.css §1 이 문자별 폴백 체인
 *   (Tiny5 → DotGothic16 → IBM Plex Sans KR)으로 재정의한다.
 *   색·간격·타이포 스케일·radius 는 y2k 를 따른다.
 * ⚠ y2k 의 radius 는 **전부 0px** 이다 — 각진 모서리가 이 테마의 성격이라
 *   카드·버튼·입력·칩·썸네일은 각진 채로 둔다.
 *   다만 tokens.css 가 `--radius-full` **하나만** 별칭에서 끊어 9999 로 되돌린다
 *   (2026-08-12, B안). 아바타·배지·스캔 FAB 처럼 **모양이 뜻을 나르는** 자리 때문이다.
 *   크기 단계(inner/element/container/page)는 그대로 테마를 따른다.
 */
import { y2kTheme } from "@astryxdesign/theme-y2k/built";
import type { ComponentProps } from "react";
import type { Theme } from "@astryxdesign/core/theme";

export type AstryxTheme = ComponentProps<typeof Theme>["theme"];
export type ThemeMode = "light" | "dark";

export interface ThemeCatalogEntry {
  key: string;
  label: string;
  /** gothic 은 라이트 팔레트가 없다 — 모드 토글을 dark 로 고정한다. */
  darkOnly?: boolean;
  /** 배포 테마는 정적 로드. 나머지는 dev 에서만 dynamic import. */
  load: () => Promise<AstryxTheme>;
}

/** 배포 기본 테마 — 첫 페인트에 쓰이므로 정적 참조여야 한다. */
export const DEFAULT_THEME_KEY = "y2k";
export const DEFAULT_THEME: AstryxTheme = y2kTheme;

/**
 * 테마 전환 UI 노출 여부 — 개발 빌드 전용. Vite 가 프로덕션에서 false 로 치환한다.
 * 2026-09-15: 데스크탑 Appearance 절을 개발 빌드에서도 숨긴다(사용자 요청).
 * 다시 켜려면 THEME_SWITCHER_VISIBLE 를 true 로.
 */
const THEME_SWITCHER_VISIBLE: boolean = false;
export const THEME_SWITCHER_ENABLED = THEME_SWITCHER_VISIBLE && import.meta.env.DEV;

/**
 * 비교용 테마 6종은 이 배열 안에서만 dynamic import 된다.
 * THEME_SWITCHER_ENABLED 가 false 로 치환되면 배열 전체가 도달 불가 → Rollup 이
 * 청크째 드롭한다 (프로덕션 dist 에 source-*.js 테마 청크가 생기지 않는 것으로 검증).
 */
const DEV_THEMES: ThemeCatalogEntry[] = THEME_SWITCHER_ENABLED
  ? [
      { key: "stone", label: "Stone", load: async () => (await import("@astryxdesign/theme-stone")).stoneTheme },
      { key: "neutral", label: "Neutral", load: async () => (await import("@astryxdesign/theme-neutral")).neutralTheme },
      { key: "butter", label: "Butter", load: async () => (await import("@astryxdesign/theme-butter")).butterTheme },
      { key: "chocolate", label: "Chocolate", load: async () => (await import("@astryxdesign/theme-chocolate")).chocolateTheme },
      { key: "matcha", label: "Matcha", load: async () => (await import("@astryxdesign/theme-matcha")).matchaTheme },
      { key: "gothic", label: "Gothic", darkOnly: true, load: async () => (await import("@astryxdesign/theme-gothic")).gothicTheme },
    ]
  : [];

export const THEME_CATALOG: ThemeCatalogEntry[] = [
  { key: "y2k", label: "Y2K", load: async () => y2kTheme },
  ...DEV_THEMES,
];

export function findThemeEntry(key: string): ThemeCatalogEntry {
  return THEME_CATALOG.find((t) => t.key === key) ?? THEME_CATALOG[0];
}
